import React, { useState, useEffect } from 'react';
import { PageSpeedResult } from '../../../types';
import { ALL_CATEGORIES_KOREAN } from '../../../utils/categoryMappings';
import { calculateMobileRating, calculateDesktopRating, getRatingColor } from '../../../utils/overallRating';

interface HallOfFameData {
  siteId?: number; // 데이터베이스 ID (보고서 조회용)
  siteName: string;
  browserTitle?: string; // 브라우저 타이틀 (옵셔널)
  url: string;
  logo: string;
  desktopScore: number;
  mobileScore: number;
  overallRating: number; // 자체 오버롤 레이팅 (0-100)
  ratingGrade: string;   // 등급 (A+, A, B+ 등)
  category: string;
  description: string;
  performance: {
    desktop: {
      totalSize: number;
      resourceCount: number;
      imageSize: number;
      fcp: number;
      tbt: number;
    };
    mobile: {
      totalSize: number;
      resourceCount: number;
      imageSize: number;
      fcp: number;
      tbt: number;
    };
  };
}

interface HallOfFameModalProps {
  showHallOfFame: boolean;
  setShowHallOfFame: (show: boolean) => void;
  modalOrigin: { x: number; y: number };
  currentSiteUrl?: string;
  currentSiteData?: PageSpeedResult | null;
  llmAnalysis?: string | null;
  performanceScores?: { desktop: number; mobile: number };
  onViewReport?: (siteId: number, url: string, siteName: string) => void; // 보고서 보기 핸들러
}

const HallOfFameModal: React.FC<HallOfFameModalProps> = ({
  showHallOfFame,
  setShowHallOfFame,
  modalOrigin,
  currentSiteUrl,
  currentSiteData,
  llmAnalysis,
  performanceScores,
  onViewReport
}) => {
  // 카테고리 탭 상태 관리
  const [activeCategory, setActiveCategory] = useState<string>('전체');
  // 실제 데이터베이스 데이터 상태
  const [hallOfFameData, setHallOfFameData] = useState<HallOfFameData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // 카테고리별 통계 상태
  const [categoryStats, setCategoryStats] = useState<Record<string, number>>({});
  // 전체보기 토글 상태
  const [showAll, setShowAll] = useState<boolean>(false);
  
  // 모달 크기 조절 상태 - localStorage에서 저장된 크기 복원 또는 기본값 사용
  const getInitialModalSize = () => {
    try {
      const saved = localStorage.getItem('hallOfFameModalSize');
      if (saved) {
        const parsed = JSON.parse(saved);
        // 화면 크기보다 큰 경우 조정
        return {
          width: Math.min(parsed.width, window.innerWidth - 100),
          height: Math.min(parsed.height, window.innerHeight * 1.5)
        };
      }
    } catch (error) {
      console.log('Failed to load saved modal size');
    }
    // 기본값
    return {
      width: Math.min(window.innerWidth - 100, 1600),
      height: Math.min(window.innerHeight - 150, 1000)
    };
  };

  const [modalSize, setModalSize] = useState(getInitialModalSize);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const [justFinishedResizing, setJustFinishedResizing] = useState(false);

  // 도움말 상태 관리
  const [showHelp, setShowHelp] = useState<string | null>(null);

  // 현재 사이트 오버롤 레이팅
  const [currentSiteRating, setCurrentSiteRating] = useState<number>(0);

  // 도움말 컨텐츠 정의 (간단한 정의만)
  const helpContent: Record<string, string> = {
    'site-info': '사이트 이름과 URL을 표시하는 영역입니다.',
    'pc-score': '자체 오버롤 레이팅 점수입니다. 실제 성능 지표를 기반으로 한 0-100점 상대평가입니다.',
    'mobile-score': '모바일 환경에서의 PageSpeed 성능 점수입니다.',
    'pc-size': 'PC 버전 웹사이트의 총 용량입니다.',
    'mobile-size': '모바일 버전 웹사이트의 총 용량입니다.',
    'pc-fcp': 'PC에서 첫 컨텐츠 표시 시간입니다.',
    'mobile-fcp': '모바일에서 첫 컨텐츠 표시 시간입니다.',
    'pc-tbt': 'PC에서 메인 스레드 차단 시간입니다.',
    'mobile-tbt': '모바일에서 메인 스레드 차단 시간입니다.'
  };

  // 오버롤 레이팅 색상 반환
  const getRatingColor = (rating: number): string => {
    if (rating >= 90) return '#00C851'; // 초록
    if (rating >= 80) return '#39C0ED'; // 파랑
    if (rating >= 70) return '#FFB74D'; // 주황
    if (rating >= 60) return '#FF8A65'; // 연한 빨강
    if (rating >= 50) return '#FF7043'; // 빨강
    if (rating >= 40) return '#F44336'; // 진한 빨강
    return '#9E9E9E'; // 회색
  };

  // 도움말 아이콘 컴포넌트
  const HelpIcon = ({ helpKey }: { helpKey: string }) => (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '4px' }}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          setShowHelp(showHelp === helpKey ? null : helpKey);
        }}
        style={{
          cursor: 'pointer',
          color: '#6b7280',
          fontSize: '12px',
          display: 'inline-block',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          border: '1px solid #6b7280',
          textAlign: 'center',
          lineHeight: '12px',
          userSelect: 'none',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#6b7280';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#6b7280';
        }}
        title="도움말 보기"
      >
        ?
      </span>
      {showHelp === helpKey && (
        <div style={{
          position: 'absolute',
          top: '25px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#333',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          minWidth: '150px',
          maxWidth: '250px',
          zIndex: 10001,
          whiteSpace: 'nowrap',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: '1px solid #555'
        }}>
          {helpContent[helpKey]}
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '0',
            height: '0',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid #333'
          }}></div>
        </div>
      )}
    </span>
  );
  
  // 파비콘 URL 생성 함수
  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return '🌐'; // 파비콘 로드 실패시 기본 이모지
    }
  };

  // LLM 분석에서 페이지 타이틀 추출 함수
  const extractTitleFromLLM = (analysis: string | null | undefined): string | null => {
    // 타입 안전성 검증: analysis가 문자열인지 확인
    if (!analysis || typeof analysis !== 'string') {
      console.warn('extractTitleFromLLM: analysis is not a string:', typeof analysis, analysis);
      return null;
    }

    // "페이지 제목이 "메디큐브 - MEDICUBE"로" 같은 패턴에서 타이틀 추출
    const titlePatterns = [
      /페이지 제목이\s*"([^"]+)"/i,
      /제목이\s*"([^"]+)"/i,
      /페이지 제목[:\s]*"([^"]+)"/i,
      /제목[:\s]*"([^"]+)"/i
    ];

    for (const pattern of titlePatterns) {
      const match = analysis.match(pattern);
      if (match && match[1] && match[1].trim() !== '') {
        return match[1].trim();
      }
    }

    return null;
  };

  // 사이트 타이틀 추출 함수
  const getSiteTitle = (url: string, siteData?: PageSpeedResult | null, llmAnalysis?: string | null) => {
    // 1. LLM 분석에서 타이틀 추출 시도
    if (llmAnalysis) {
      const extractedTitle = extractTitleFromLLM(llmAnalysis);
      if (extractedTitle) {
        return extractedTitle.length > 30 ? extractedTitle.substring(0, 30) + '...' : extractedTitle;
      }
    }

    // 2. 폴백: URL에서 도메인명 추출
    try {
      const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
      return domain.replace('www.', '').split('.')[0].toUpperCase();
    } catch {
      return '분석 사이트';
    }
  };

  // 크기 조절 이벤트 핸들러들
  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing || !resizeDirection) return;
    
    e.preventDefault();
    const modalEl = document.querySelector('.modal-container') as HTMLElement;
    if (!modalEl) return;

    const rect = modalEl.getBoundingClientRect();
    const { clientX, clientY } = e;

    let newWidth = modalSize.width;
    let newHeight = modalSize.height;

    // 방향에 따른 크기 계산
    if (resizeDirection === 'right' || resizeDirection === 'corner-rb') {
      newWidth = Math.max(800, clientX - rect.left);
    }
    if (resizeDirection === 'left' || resizeDirection === 'corner-lb') {
      newWidth = Math.max(800, rect.right - clientX);
    }
    if (resizeDirection === 'bottom' || resizeDirection === 'corner-rb' || resizeDirection === 'corner-lb') {
      newHeight = Math.max(600, clientY - rect.top);
    }
    if (resizeDirection === 'top' || resizeDirection === 'corner-rt' || resizeDirection === 'corner-lt') {
      newHeight = Math.max(600, rect.bottom - clientY);
    }

    // 최대 크기 제한 - 세로 제한 해제, 가로는 화면 크기에 맞춤
    newWidth = Math.min(newWidth, window.innerWidth - 100);
    newHeight = Math.min(newHeight, window.innerHeight * 1.5); // 세로는 화면 높이의 1.5배까지 허용

    const newSize = { width: newWidth, height: newHeight };
    setModalSize(newSize);
    
    // localStorage에 크기 저장
    try {
      localStorage.setItem('hallOfFameModalSize', JSON.stringify(newSize));
    } catch (error) {
      console.log('Failed to save modal size');
    }
  };

  const handleResizeEnd = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(false);
    setResizeDirection(null);
    setJustFinishedResizing(true);
    
    // 200ms 후에 배경 클릭 허용
    setTimeout(() => {
      setJustFinishedResizing(false);
    }, 200);
  };

  // 크기 조절 이벤트 리스너 등록/해제
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      // 커서 스타일 설정
      if (resizeDirection === 'corner-rb') {
        document.body.style.cursor = 'nw-resize';
      } else if (resizeDirection === 'right') {
        document.body.style.cursor = 'ew-resize';
      } else if (resizeDirection === 'bottom') {
        document.body.style.cursor = 'ns-resize';
      }
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, resizeDirection, modalSize]);
  
  // 모달이 처음 열릴 때만 카테고리 통계 가져오기
  useEffect(() => {
    if (showHallOfFame) {
      fetchCategoryStats();
    }
  }, [showHallOfFame]); // 크기 설정 제거, 저장된 크기 사용

  // 카테고리 또는 전체보기 변경 시 데이터 다시 가져오기
  useEffect(() => {
    if (showHallOfFame) {
      fetchHallOfFameData();
    }
  }, [showHallOfFame, activeCategory, showAll]);
  
  // 카테고리별 통계 데이터 가져오기
  const fetchCategoryStats = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '' 
        : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
      const response = await fetch(`${apiUrl}/api/hall-of-fame/stats`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // 이미 한글 카테고리가 DB에 저장되어 있으므로 직접 사용
          const koreanStats: Record<string, number> = {};
          let totalCount = 0;
          
          result.data.forEach((stat: { category: string; total_sites: string; sites_with_data: string }) => {
            // total_sites를 사용하여 전체 사이트 수 표시
            const count = parseInt(stat.total_sites) || 0;
            koreanStats[stat.category] = count;
            totalCount += count;
          });
          
          koreanStats['전체'] = totalCount;
          setCategoryStats(koreanStats);
        }
      }
    } catch (error) {
      console.error('❌ 카테고리 통계 로딩 실패:', error);
    }
  };

  const fetchHallOfFameData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 프로덕션에서는 상대 경로 사용, 개발에서는 localhost
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '' 
        : (process.env.REACT_APP_API_URL || 'http://localhost:3300');
      // 한글 카테고리를 직접 사용해서 API 호출 (DB에 한글로 저장됨)
      const params = new URLSearchParams();
      if (activeCategory !== '전체') {
        params.append('category', activeCategory);
      }
      params.append('showAll', showAll.toString());
      params.append('limit', showAll ? '100' : '10'); // 전체보기면 더 많이, 아니면 10개

      const response = await fetch(`${apiUrl}/api/hall-of-fame?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('명예의전당 데이터를 불러오는데 실패했습니다.');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        // 데이터베이스 형식을 컴포넌트 형식으로 변환
        const transformedData: HallOfFameData[] = result.data.map((item: any) => ({
          siteId: item.site_id || item.id, // 데이터베이스 ID 추가
          siteName: item.site_name,
          browserTitle: item.browser_title,
          url: item.url.replace(/^https?:\/\//, ''),
          logo: item.logo_emoji || '🌐',
          desktopScore: item.desktop_rating || item.performance_desktop || 0,
          mobileScore: item.mobile_rating || item.performance_mobile || 0,
          overallRating: item.overall_rating || 0, // 백엔드에서 계산된 오버롤 레이팅
          ratingGrade: item.rating_grade || 'D',   // 백엔드에서 계산된 등급
          category: item.category, // 이미 DB에 한글로 저장됨
          description: item.description || '',
          performance: {
            desktop: {
              totalSize: item.total_size_desktop || 0,
              resourceCount: 0,
              imageSize: 0,
              fcp: item.fcp_desktop || 0,
              tbt: item.tbt_desktop || 0
            },
            mobile: {
              totalSize: item.total_size_mobile || 0,
              resourceCount: 0,
              imageSize: 0,
              fcp: item.fcp_mobile || 0,
              tbt: item.tbt_mobile || 0
            }
          }
        }));
        
        setHallOfFameData(transformedData);

        // 현재 사이트의 오버롤 레이팅 계산 (V1.1 비선형 스케일링)
        if (currentSiteData) {
          const currentSiteMetrics = {
            fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
            lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
            tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
            total_size_mobile: currentSiteData.mobile?.pageSize.totalSize,
            fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
            lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
            tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
            total_size_desktop: currentSiteData.desktop?.pageSize.totalSize
          };

          const mobileRating = calculateMobileRating(currentSiteMetrics, transformedData);
          const desktopRating = calculateDesktopRating(currentSiteMetrics, transformedData);
          const rating = Math.round((mobileRating + desktopRating) / 2);
          setCurrentSiteRating(rating);
        }
      } else {
        // 데이터가 없으면 샘플 데이터 사용 (폴백)
        const fallbackData = getFallbackData();
        setHallOfFameData(fallbackData);

        // 폴백 데이터로도 현재 사이트 레이팅 계산 (V1.1 비선형 스케일링)
        if (currentSiteData) {
          const currentSiteMetrics = {
            fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
            lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
            tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
            total_size_mobile: currentSiteData.mobile?.pageSize.totalSize,
            fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
            lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
            tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
            total_size_desktop: currentSiteData.desktop?.pageSize.totalSize
          };

          const mobileRating = calculateMobileRating(currentSiteMetrics, fallbackData);
          const desktopRating = calculateDesktopRating(currentSiteMetrics, fallbackData);
          const rating = Math.round((mobileRating + desktopRating) / 2);
          setCurrentSiteRating(rating);
        }
      }
    } catch (err) {
      console.error('❌ 명예의전당 데이터 로딩 실패:', err);
      setError('데이터를 불러오는데 실패했습니다. 샘플 데이터를 표시합니다.');
      // 에러 시 샘플 데이터 사용
      setHallOfFameData(getFallbackData());
    } finally {
      setIsLoading(false);
    }
  };
  
  // 폴백용 샘플 데이터
  const getFallbackData = (): HallOfFameData[] => [
    {
      siteName: '글로벌 럭셔리몰',
      url: 'luxury-store.cafe24.com',
      logo: '💎',
      desktopScore: 98,
      mobileScore: 95,
      overallRating: 95,
      ratingGrade: 'A+',
      category: '럭셔리/패션',
      description: '최고급 브랜드를 위한 최적화된 쇼핑 경험',
      performance: {
        desktop: {
          totalSize: 1850, // KB
          resourceCount: 28,
          imageSize: 420, // KB
          fcp: 0.9, // seconds
          tbt: 140 // ms
        },
        mobile: {
          totalSize: 1650, // KB
          resourceCount: 26,
          imageSize: 380, // KB
          fcp: 1.2, // seconds
          tbt: 180 // ms
        }
      }
    },
    {
      siteName: '스마트 테크몰',
      url: 'smarttech.cafe24.com',
      logo: '📱',
      desktopScore: 96,
      mobileScore: 94,
      overallRating: 92,
      ratingGrade: 'A+',
      category: '전자제품',
      description: '모바일 퍼스트 최적화로 빠른 구매 경험',
      performance: {
        desktop: {
          totalSize: 2100,
          resourceCount: 32,
          imageSize: 680,
          fcp: 1.0,
          tbt: 160
        },
        mobile: {
          totalSize: 1890,
          resourceCount: 29,
          imageSize: 620,
          fcp: 1.3,
          tbt: 220
        }
      }
    },
    {
      siteName: '오가닉 라이프',
      url: 'organic-life.cafe24.com',
      logo: '🌱',
      desktopScore: 97,
      mobileScore: 93,
      overallRating: 90,
      ratingGrade: 'A+',
      category: '건강/뷰티',
      description: '친환경 제품을 위한 깔끔한 쇼핑몰',
      performance: {
        desktop: {
          totalSize: 1620,
          resourceCount: 24,
          imageSize: 350,
          fcp: 0.8,
          tbt: 120
        },
        mobile: {
          totalSize: 1480,
          resourceCount: 22,
          imageSize: 320,
          fcp: 1.1,
          tbt: 170
        }
      }
    },
    {
      siteName: '키즈 월드',
      url: 'kidsworld.cafe24.com',
      logo: '🧸',
      desktopScore: 95,
      mobileScore: 92,
      overallRating: 88,
      ratingGrade: 'A',
      category: '유아/아동',
      description: '부모와 아이가 함께 즐기는 쇼핑',
      performance: {
        desktop: {
          totalSize: 2350,
          resourceCount: 38,
          imageSize: 890,
          fcp: 1.1,
          tbt: 190
        },
        mobile: {
          totalSize: 2120,
          resourceCount: 35,
          imageSize: 820,
          fcp: 1.4,
          tbt: 250
        }
      }
    },
    {
      siteName: '홈 데코샵',
      url: 'homedeco.cafe24.com',
      logo: '🏠',
      desktopScore: 94,
      mobileScore: 91,
      overallRating: 85,
      ratingGrade: 'A',
      category: '홈인테리어',
      description: '집을 꾸미는 모든 것을 한 곳에',
      performance: {
        desktop: {
          totalSize: 2680,
          resourceCount: 42,
          imageSize: 1150,
          fcp: 1.2,
          tbt: 210
        },
        mobile: {
          totalSize: 2420,
          resourceCount: 39,
          imageSize: 1050,
          fcp: 1.5,
          tbt: 280
        }
      }
    },
    {
      siteName: '스포츠 기어',
      url: 'sportsgear.cafe24.com',
      logo: '🏃‍♂️',
      desktopScore: 96,
      mobileScore: 94,
      overallRating: 82,
      ratingGrade: 'A',
      category: '스포츠/레저',
      description: '운동을 사랑하는 사람들의 선택',
      performance: {
        desktop: {
          totalSize: 1950,
          resourceCount: 30,
          imageSize: 520,
          fcp: 0.9,
          tbt: 150
        },
        mobile: {
          totalSize: 1780,
          resourceCount: 28,
          imageSize: 480,
          fcp: 1.2,
          tbt: 200
        }
      }
    },
    {
      siteName: '푸드 마켓',
      url: 'foodmarket.cafe24.com',
      logo: '🍯',
      desktopScore: 93,
      mobileScore: 90,
      overallRating: 78,
      ratingGrade: 'B+',
      category: '식품/음료',
      description: '신선한 식재료와 건강한 먹거리',
      performance: {
        desktop: {
          totalSize: 2890,
          resourceCount: 45,
          imageSize: 1350,
          fcp: 1.3,
          tbt: 230
        },
        mobile: {
          totalSize: 2650,
          resourceCount: 42,
          imageSize: 1250,
          fcp: 1.6,
          tbt: 300
        }
      }
    },
    {
      siteName: '아트 갤러리샵',
      url: 'artgallery.cafe24.com',
      logo: '🎨',
      desktopScore: 95,
      mobileScore: 92,
      overallRating: 75,
      ratingGrade: 'B+',
      category: '예술/문화',
      description: '예술 작품과 문화 상품의 특별한 공간',
      performance: {
        desktop: {
          totalSize: 2200,
          resourceCount: 35,
          imageSize: 780,
          fcp: 1.0,
          tbt: 170
        },
        mobile: {
          totalSize: 2000,
          resourceCount: 32,
          imageSize: 720,
          fcp: 1.3,
          tbt: 220
        }
      }
    }
  ];

  // 모든 카테고리 목록 (항상 표시)
  const getAllCategories = () => {
    return ALL_CATEGORIES_KOREAN;
  };

  const getFilteredData = () => {
    if (activeCategory === '전체') {
      return hallOfFameData;
    }
    return hallOfFameData.filter(site => site.category === activeCategory);
  };

  const categories = getAllCategories();
  const filteredData = getFilteredData();

  if (!showHallOfFame) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '1000px 40px 40px 40px',
        backdropFilter: 'blur(5px)'
      }}
      onMouseDown={(e) => {
        // 크기 조절 중이거나 방금 끝났으면 배경 클릭 무시
        if (isResizing || justFinishedResizing) return;
        // 마우스 다운 이벤트로 더 확실한 배경 클릭 감지
        if (e.target === e.currentTarget) {
          setShowHallOfFame(false);
        }
      }}
      onClick={(e) => {
        // 크기 조절 중이거나 방금 끝났으면 배경 클릭 무시
        if (isResizing || justFinishedResizing) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // 모든 배경 영역 클릭 시 도움말 닫기 및 모달 닫기
        if (e.target === e.currentTarget) {
          setShowHelp(null);
          setShowHallOfFame(false);
        }
      }}
    >
      <div 
        className="modal-container"
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          width: `${modalSize.width}px`,
          height: `${modalSize.height}px`,
          maxWidth: '95vw',
          // maxHeight 제한 제거로 세로 확장 자유롭게
          minWidth: '800px',
          minHeight: '600px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          animation: 'slideFromButton 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transformOrigin: 'center center',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {/* 크기 조절 핸들들 */}
        {/* 우측 핸들 */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '0px',
            bottom: '10px',
            width: '8px',
            cursor: 'ew-resize',
            backgroundColor: isResizing && resizeDirection === 'right' ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
            borderRadius: '0 20px 20px 0',
            zIndex: 20
          }}
          onMouseDown={(e) => handleResizeStart(e, 'right')}
        />
        {/* 하단 핸들 */}
        <div
          style={{
            position: 'absolute',
            left: '10px',
            right: '10px',
            bottom: '0px',
            height: '8px',
            cursor: 'ns-resize',
            backgroundColor: isResizing && resizeDirection === 'bottom' ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
            borderRadius: '0 0 20px 20px',
            zIndex: 20
          }}
          onMouseDown={(e) => handleResizeStart(e, 'bottom')}
        />
        {/* 우측 하단 코너 핸들 */}
        <div
          style={{
            position: 'absolute',
            right: '0px',
            bottom: '0px',
            width: '20px',
            height: '20px',
            cursor: 'nw-resize',
            backgroundColor: isResizing && resizeDirection === 'corner-rb' ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
            borderRadius: '0 0 20px 0',
            zIndex: 21
          }}
          onMouseDown={(e) => handleResizeStart(e, 'corner-rb')}
        />
        {/* 크기 조절 표시 아이콘 */}
        <div
          style={{
            position: 'absolute',
            right: '5px',
            bottom: '5px',
            width: '12px',
            height: '12px',
            pointerEvents: 'none',
            opacity: 0.4,
            zIndex: 22
          }}
        >
          <div style={{
            position: 'absolute',
            right: '0px',
            bottom: '0px',
            width: '3px',
            height: '3px',
            backgroundColor: '#6b7280',
            borderRadius: '1px'
          }} />
          <div style={{
            position: 'absolute',
            right: '0px',
            bottom: '5px',
            width: '3px',
            height: '7px',
            background: 'linear-gradient(to top, #6b7280 0%, #6b7280 3px, transparent 3px)',
            borderRadius: '1px'
          }} />
          <div style={{
            position: 'absolute',
            right: '5px',
            bottom: '0px',
            width: '7px',
            height: '3px',
            background: 'linear-gradient(to right, #6b7280 0%, #6b7280 3px, transparent 3px)',
            borderRadius: '1px'
          }} />
        </div>
        {/* 모달 헤더 */}
        <div style={{
          padding: '30px 30px 20px 30px',
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          borderRadius: '24px 24px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h2 style={{
                margin: 0,
                color: '#92400e',
                fontSize: '28px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                🏆 명예의 전당
              </h2>
              <p style={{
                margin: '8px 0 0 0',
                color: '#78350f',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                카페24 최고 성능을 자랑하는 쇼핑몰과 비교해보세요
              </p>
            </div>
            <button
              onClick={() => setShowHallOfFame(false)}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(146, 64, 14, 0.3)',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                color: '#92400e',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ✕
            </button>
          </div>
        </div>


        {/* 모달 콘텐츠 */}
        <div style={{
          padding: '30px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* 현재 분석 사이트 고정 영역 */}
          {currentSiteUrl && currentSiteData && (
            <div style={{
              backgroundColor: '#dbeafe',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '20px',
              border: '2px solid #3b82f6',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 15px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                {currentSiteUrl ? (
                  <>
                    <img 
                      src={getFaviconUrl(currentSiteUrl)} 
                      alt="favicon"
                      style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '4px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0'
                      }}
                      onError={(e) => {
                        // 파비콘 로드 실패시 기본 이모지로 대체
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        target.nextSibling && ((target.nextSibling as HTMLElement).style.display = 'inline');
                      }}
                    />
                    <span style={{ display: 'none' }}>🌐</span>
                    {getSiteTitle(currentSiteUrl, currentSiteData, llmAnalysis)}
                  </>
                ) : (
                  <>🎯 현재 분석 사이트</>
                )}
              </h3>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '15px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1e40af',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                wordBreak: 'break-all',
                marginBottom: '15px'
              }}>
                {currentSiteUrl}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>PC 점수</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getRatingColor((() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
                          lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
                          tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
                          total_size_desktop: currentSiteData.desktop?.pageSize?.totalSize
                        };
                        return performanceScores?.desktop || 0;
                      }
                      return currentSiteRating;
                    })()),
                    borderRadius: '4px',
                    padding: '2px 6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}>
                    {(() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
                          lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
                          tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
                          total_size_desktop: currentSiteData.desktop?.pageSize?.totalSize
                        };
                        return performanceScores?.desktop || 0;
                      }
                      return currentSiteRating;
                    })()}점
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>모바일 점수</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getRatingColor((() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
                          lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
                          tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
                          total_size_mobile: currentSiteData.mobile?.pageSize?.totalSize
                        };
                        return performanceScores?.mobile || 0;
                      }
                      return currentSiteRating;
                    })()),
                    borderRadius: '4px',
                    padding: '2px 6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}>
                    {(() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
                          lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
                          tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
                          total_size_mobile: currentSiteData.mobile?.pageSize?.totalSize
                        };
                        return performanceScores?.mobile || 0;
                      }
                      return currentSiteRating;
                    })()}점
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>PC 용량</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                    {currentSiteData?.desktop?.pageSize?.totalSize 
                      ? (currentSiteData?.desktop?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                      : 'N/A'}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>모바일 용량</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                    {currentSiteData?.mobile?.pageSize?.totalSize 
                      ? (currentSiteData?.mobile?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                      : 'N/A'}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>PC FCP</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed' }}>
                    {currentSiteData?.desktop?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>모바일 FCP</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed' }}>
                    {currentSiteData?.mobile?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>PC TBT</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#ea580c' }}>
                    {currentSiteData?.desktop?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>모바일 TBT</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#ea580c' }}>
                    {currentSiteData?.mobile?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 카테고리 탭 네비게이션 */}
          <div 
            style={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: '6px',
              marginBottom: '20px',
              padding: '12px 15px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',
              WebkitOverflowScrolling: 'touch'
            }}
            className="category-scroll"
          >
            <style>{`
              .category-scroll::-webkit-scrollbar {
                height: 6px;
              }
              .category-scroll::-webkit-scrollbar-track {
                background: transparent;
              }
              .category-scroll::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 3px;
              }
              .category-scroll::-webkit-scrollbar-thumb:hover {
                background: #94a3b8;
              }
            `}</style>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  flexShrink: 0,
                  backgroundColor: activeCategory === category ? '#3b82f6' : '#ffffff',
                  color: activeCategory === category ? '#ffffff' : '#374151',
                  boxShadow: activeCategory === category 
                    ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  border: activeCategory === category ? '2px solid #2563eb' : '2px solid #e2e8f0'
                }}
                onMouseEnter={(e) => {
                  if (activeCategory !== category) {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeCategory !== category) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {category}
                <span style={{
                  marginLeft: '6px',
                  fontSize: '11px',
                  opacity: 0.7,
                  backgroundColor: activeCategory === category ? 'rgba(255,255,255,0.2)' : 'rgba(59,130,246,0.1)',
                  padding: '2px 6px',
                  borderRadius: '10px'
                }}>
                  {categoryStats[category] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* 전체보기 토글 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '15px 0 10px 0',
            padding: '0 5px'
          }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {showAll ? `전체 사이트 (${hallOfFameData.length}개)` : `상위 10개 사이트`}
            </span>
            <button
              onClick={() => setShowAll(!showAll)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '2px solid #3b82f6',
                backgroundColor: showAll ? '#3b82f6' : 'white',
                color: showAll ? 'white' : '#3b82f6',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              onMouseEnter={(e) => {
                if (!showAll) {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (!showAll) {
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              {showAll ? '상위 10개만 보기' : '전체 보기'}
            </button>
          </div>

          {/* 테이블 뷰 */}
          <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e2e8f0',
              flex: 1,
              minHeight: 0
            }}>
              <div style={{
                overflowX: 'auto',
                height: '100%',
                overflowY: 'auto'
              }}>
                <table style={{
                  width: '100%',
                  minWidth: '1400px',
                  borderCollapse: 'collapse',
                  fontSize: '13px'
                }}>
                  <thead>
                    <tr style={{
                      backgroundColor: '#f8fafc',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1
                    }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '200px',
                        width: '200px'
                      }}>
                        사이트 정보
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        PC 점수<HelpIcon helpKey="pc-score" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        모바일 점수<HelpIcon helpKey="mobile-score" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '120px',
                        width: '120px'
                      }}>
                        PC 용량<HelpIcon helpKey="pc-size" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '120px',
                        width: '120px'
                      }}>
                        모바일 용량<HelpIcon helpKey="mobile-size" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        PC FCP<HelpIcon helpKey="pc-fcp" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        모바일 FCP<HelpIcon helpKey="mobile-fcp" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        PC TBT<HelpIcon helpKey="pc-tbt" />
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        borderBottom: '2px solid #e2e8f0',
                        minWidth: '100px',
                        width: '100px'
                      }}>
                        모바일 TBT<HelpIcon helpKey="mobile-tbt" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 현재 분석된 사이트를 맨 위에 표시 - 고정 영역으로 이동됨 */}
                    {false && currentSiteUrl && currentSiteData && (
                      <tr
                        style={{
                          backgroundColor: '#dbeafe',
                          borderBottom: '2px solid #3b82f6',
                          fontWeight: '600',
                          position: 'sticky',
                          top: '48px',
                          zIndex: 2,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <td style={{ padding: '16px', minWidth: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              fontSize: '20px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '8px',
                              padding: '4px',
                              border: '1px solid #2563eb'
                            }}>
                              🎯
                            </div>
                            <div>
                              <div style={{
                                fontWeight: '700',
                                color: '#1e40af',
                                fontSize: '14px',
                                marginBottom: '2px'
                              }}>
                                현재 분석 사이트
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#1e40af',
                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                wordBreak: 'break-all'
                              }}>
                                {currentSiteUrl}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: getRatingColor((() => {
                              if (currentSiteData && currentSiteUrl) {
                                const hallOfFameReference = [
                                  { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                                    fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                                  { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                                    fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                                  { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                                    fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                                ];
                                const currentSiteMetrics = {
                                  fcp_desktop: currentSiteData?.desktop?.firstContentfulPaint,
                                  lcp_desktop: currentSiteData?.desktop?.largestContentfulPaint,
                                  tbt_desktop: currentSiteData?.desktop?.totalBlockingTime,
                                  total_size_desktop: currentSiteData?.desktop?.pageSize?.totalSize
                                };
                                return performanceScores?.desktop || 0;
                              }
                              return currentSiteRating;
                            })()),
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {(() => {
                              if (currentSiteData && currentSiteUrl) {
                                const hallOfFameReference = [
                                  { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                                    fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                                  { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                                    fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                                  { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                                    fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                                ];
                                const currentSiteMetrics = {
                                  fcp_desktop: currentSiteData?.desktop?.firstContentfulPaint,
                                  lcp_desktop: currentSiteData?.desktop?.largestContentfulPaint,
                                  tbt_desktop: currentSiteData?.desktop?.totalBlockingTime,
                                  total_size_desktop: currentSiteData?.desktop?.pageSize?.totalSize
                                };
                                return performanceScores?.desktop || 0;
                              }
                              return currentSiteRating;
                            })()}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: getRatingColor((() => {
                              if (currentSiteData && currentSiteUrl) {
                                const hallOfFameReference = [
                                  { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                                    fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                                  { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                                    fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                                  { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                                    fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                                ];
                                const currentSiteMetrics = {
                                  fcp_mobile: currentSiteData?.mobile?.firstContentfulPaint,
                                  lcp_mobile: currentSiteData?.mobile?.largestContentfulPaint,
                                  tbt_mobile: currentSiteData?.mobile?.totalBlockingTime,
                                  total_size_mobile: currentSiteData?.mobile?.pageSize?.totalSize
                                };
                                return performanceScores?.mobile || 0;
                              }
                              return currentSiteRating;
                            })()),
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {(() => {
                              if (currentSiteData && currentSiteUrl) {
                                const hallOfFameReference = [
                                  { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                                    fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                                  { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                                    fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                                  { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                                    fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                                ];
                                const currentSiteMetrics = {
                                  fcp_mobile: currentSiteData?.mobile?.firstContentfulPaint,
                                  lcp_mobile: currentSiteData?.mobile?.largestContentfulPaint,
                                  tbt_mobile: currentSiteData?.mobile?.totalBlockingTime,
                                  total_size_mobile: currentSiteData?.mobile?.pageSize?.totalSize
                                };
                                return performanceScores?.mobile || 0;
                              }
                              return currentSiteRating;
                            })()}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#ecfdf5',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#059669',
                            border: '1px solid #bbf7d0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.desktop?.pageSize?.totalSize 
                              ? (currentSiteData?.desktop?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                              : 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#ecfdf5',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#059669',
                            border: '1px solid #bbf7d0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.mobile?.pageSize?.totalSize 
                              ? (currentSiteData?.mobile?.pageSize?.totalSize! / (1024 * 1024)).toFixed(1) + 'MB'
                              : 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#faf5ff',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#7c3aed',
                            border: '1px solid #e9d5ff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.desktop?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#faf5ff',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#7c3aed',
                            border: '1px solid #e9d5ff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.mobile?.audits?.['first-contentful-paint']?.displayValue || 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#fff7ed',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.desktop?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#fff7ed',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {currentSiteData?.mobile?.audits?.['total-blocking-time']?.displayValue || 'N/A'}
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    {/* 구분선 - 고정 영역으로 변경되어 불필요 */}
                    {false && currentSiteUrl && currentSiteData && (
                      <tr
                        style={{
                          position: 'sticky',
                          top: '112px',
                          zIndex: 2
                        }}
                      >
                        <td colSpan={9} style={{ 
                          padding: '12px 16px', 
                          textAlign: 'center',
                          backgroundColor: '#f8fafc',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#6b7280',
                          borderBottom: '2px solid #e2e8f0',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                          ───────── 명예의 전당 사이트들 ─────────
                        </td>
                      </tr>
                    )}
                    
                    {filteredData.map((site, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td style={{ padding: '16px', minWidth: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: '#1e40af',
                                fontSize: '14px',
                                marginBottom: '2px'
                              }}>
                                {site.browserTitle || site.siteName}
                              </div>
                              <a
                                href={`https://${site.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '12px',
                                  color: '#1e40af',
                                  textDecoration: 'underline',
                                  fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  window.open(`https://${site.url}`, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                {site.url}
                              </a>
                              <div style={{
                                fontSize: '11px',
                                color: '#6b7280',
                                backgroundColor: '#dbeafe',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                marginTop: '4px',
                                display: 'inline-block'
                              }}>
                                {site.category}
                              </div>
                            </div>
                            {/* 보고서 보기 버튼 - 새 탭으로 열기 */}
                            {site.siteId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const reportUrl = `${window.location.origin}/?report=hall-of-fame&siteId=${site.siteId}`;
                                  window.open(reportUrl, '_blank', 'noopener,noreferrer');
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#2467E7',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 4px rgba(36, 103, 231, 0.2)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#1B4DB8';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(36, 103, 231, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#2467E7';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(36, 103, 231, 0.2)';
                                }}
                              >
                                📊 보고서
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: getRatingColor(site.desktopScore),
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {site.desktopScore}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: getRatingColor(site.mobileScore),
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'white',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {site.mobileScore}
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#ecfdf5',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#059669',
                            border: '1px solid #bbf7d0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {(site.performance.desktop.totalSize / (1024 * 1024)).toFixed(1)}MB
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#ecfdf5',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#059669',
                            border: '1px solid #bbf7d0',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {(site.performance.mobile.totalSize / (1024 * 1024)).toFixed(1)}MB
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#faf5ff',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#7c3aed',
                            border: '1px solid #e9d5ff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {site.performance.desktop.fcp}s
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#faf5ff',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#7c3aed',
                            border: '1px solid #e9d5ff',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {site.performance.mobile.fcp}s
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#fff7ed',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {site.performance.desktop.tbt}ms
                          </div>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <div style={{
                            backgroundColor: '#fff7ed',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#ea580c',
                            border: '1px solid #fed7aa',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}>
                            {site.performance.mobile.tbt}ms
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
          {/* 
          =====================================================
          CARDS VIEW (주석처리 - 나중에 복구 가능)
          =====================================================
          카드 뷰 (기존 레이아웃)
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '20px'
          }}>
            {hallOfFameData.map((site, index) => (
            <div
              key={index}
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                padding: '24px',
                border: '2px solid #e2e8f0',
                transition: 'all 0.3s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.borderColor = '#fbbf24';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0px)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              사이트 헤더
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1e40af'
                  }}>
                    {site.browserTitle || site.siteName}
                  </h3>
                  <a 
                    href={`https://${site.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      margin: '4px 0 0 0',
                      fontSize: '14px',
                      color: '#1e40af',
                      fontWeight: '500',
                      textDecoration: 'underline',
                      display: 'block',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      cursor: 'pointer'
                    }}
                    title={`${site.browserTitle || site.siteName} 방문하기`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      window.open(`https://${site.url}`, '_blank', 'noopener,noreferrer');
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#1e40af';
                    }}
                  >
                    {site.url}
                  </a>
                </div>
                <div style={{
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {site.category}
                </div>
              </div>

              성능 점수
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getRatingColor((() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
                          lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
                          tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
                          total_size_desktop: currentSiteData.desktop?.pageSize?.totalSize
                        };
                        return performanceScores?.desktop || 0;
                      }
                      return currentSiteRating;
                    })()),
                    borderRadius: '6px',
                    padding: '4px 8px',
                    marginBottom: '4px'
                  }}>
                    {(() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_desktop: currentSiteData.desktop?.firstContentfulPaint,
                          lcp_desktop: currentSiteData.desktop?.largestContentfulPaint,
                          tbt_desktop: currentSiteData.desktop?.totalBlockingTime,
                          total_size_desktop: currentSiteData.desktop?.pageSize?.totalSize
                        };
                        return performanceScores?.desktop || 0;
                      }
                      return currentSiteRating;
                    })()}점
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>
                    🖥️ 데스크톱
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: getRatingColor((() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
                          lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
                          tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
                          total_size_mobile: currentSiteData.mobile?.pageSize?.totalSize
                        };
                        return performanceScores?.mobile || 0;
                      }
                      return currentSiteRating;
                    })()),
                    borderRadius: '6px',
                    padding: '4px 8px',
                    marginBottom: '4px'
                  }}>
                    {(() => {
                      if (currentSiteData && currentSiteUrl) {
                        const hallOfFameReference = [
                          { fcp_mobile: 1.5, lcp_mobile: 2.1, tbt_mobile: 40, total_size_mobile: "700000",
                            fcp_desktop: 0.4, lcp_desktop: 0.5, tbt_desktop: 50, total_size_desktop: "850000" },
                          { fcp_mobile: 3.3, lcp_mobile: 4.5, tbt_mobile: 80, total_size_mobile: "3200000",
                            fcp_desktop: 0.8, lcp_desktop: 1.1, tbt_desktop: 50, total_size_desktop: "3200000" },
                          { fcp_mobile: 5.2, lcp_mobile: 9.3, tbt_mobile: 216, total_size_mobile: "2000000",
                            fcp_desktop: 0.7, lcp_desktop: 0.9, tbt_desktop: 40, total_size_desktop: "1100000" }
                        ];
                        const currentSiteMetrics = {
                          fcp_mobile: currentSiteData.mobile?.firstContentfulPaint,
                          lcp_mobile: currentSiteData.mobile?.largestContentfulPaint,
                          tbt_mobile: currentSiteData.mobile?.totalBlockingTime,
                          total_size_mobile: currentSiteData.mobile?.pageSize?.totalSize
                        };
                        return performanceScores?.mobile || 0;
                      }
                      return currentSiteRating;
                    })()}점
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>
                    📱 모바일
                  </div>
                </div>
              </div>

              성능 상세 정보
              <div style={{
                backgroundColor: 'white',
                padding: '12px',
                borderRadius: '12px',
                marginBottom: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  성능 상세 정보
                </div>
                
                PC 성능 정보
                <div style={{
                  marginBottom: '10px',
                  padding: '8px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#1e40af',
                    marginBottom: '6px'
                  }}>
                    🖥️ PC
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px',
                    fontSize: '10px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#059669' }}>
                        {(site.performance.desktop.totalSize / (1024 * 1024)).toFixed(1)}MB
                      </div>
                      <div style={{ color: '#6b7280' }}>총 용량</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#0ea5e9' }}>
                        {site.performance.desktop.resourceCount}개
                      </div>
                      <div style={{ color: '#6b7280' }}>리소스</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
                        {site.performance.desktop.imageSize}KB
                      </div>
                      <div style={{ color: '#6b7280' }}>이미지</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#7c3aed' }}>
                        {site.performance.desktop.fcp}s
                      </div>
                      <div style={{ color: '#6b7280' }}>FCP</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#ea580c' }}>
                        {site.performance.desktop.tbt}ms
                      </div>
                      <div style={{ color: '#6b7280' }}>TBT</div>
                    </div>
                  </div>
                </div>

                모바일 성능 정보
                <div style={{
                  padding: '8px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#d97706',
                    marginBottom: '6px'
                  }}>
                    📱 모바일
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px',
                    fontSize: '10px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#059669' }}>
                        {(site.performance.mobile.totalSize / (1024 * 1024)).toFixed(1)}MB
                      </div>
                      <div style={{ color: '#6b7280' }}>총 용량</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#0ea5e9' }}>
                        {site.performance.mobile.resourceCount}개
                      </div>
                      <div style={{ color: '#6b7280' }}>리소스</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
                        {site.performance.mobile.imageSize}KB
                      </div>
                      <div style={{ color: '#6b7280' }}>이미지</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#7c3aed' }}>
                        {site.performance.mobile.fcp}s
                      </div>
                      <div style={{ color: '#6b7280' }}>FCP</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', color: '#ea580c' }}>
                        {site.performance.mobile.tbt}ms
                      </div>
                      <div style={{ color: '#6b7280' }}>TBT</div>
                    </div>
                  </div>
                </div>
              </div>

              설명
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: '#4b5563',
                lineHeight: '1.5',
                fontStyle: 'italic'
              }}>
                {site.description}
              </p>
              </div>
            ))
          }
          </div>
          =====================================================
          */}

          {/* 모달 하단 정보 */}
          <div style={{
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#fef3c7',
            borderRadius: '16px',
            border: '1px solid #fbbf24'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>💡</span>
              <h4 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: '#92400e'
              }}>
                성능 최적화 팁
              </h4>
            </div>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#78350f',
              lineHeight: '1.6'
            }}>
              {activeCategory === '전체' 
                ? `총 ${filteredData.length}개의 다양한 업종 사이트들을 비교해보세요. 각 카테고리별로 특화된 최적화 기법들을 확인할 수 있습니다.`
                : `${activeCategory} 카테고리의 ${filteredData.length}개 우수 사이트들입니다. 같은 업종 내에서 벤치마킹할 수 있는 최고 수준의 성능을 확인해보세요.`
              } 공통적으로 <strong>상품 이미지 최적화</strong>, <strong>결제 프로세스 간소화</strong>, 
              <strong>카페24 CDN 활용</strong>, <strong>모바일 최적화</strong> 등을 통해 빠른 쇼핑 경험을 제공합니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HallOfFameModal;